import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { newCondition } from './advancedFilter';
import { AdvancedRow } from './AdvancedRow';
import type { IAdvancedPanelProps } from './IAdvancedPanelProps';
import { QuickFields } from './QuickFields';
import './advanced.css';

/**
 * Панель розширених фільтрів під рядком фільтрів (UIUX patterns/filtering, прототип v4): заголовок «Розширені фільтри · N записів
 * відповідають · застосовується до всіх подань цього модуля», «Очистити умови» (є рядки), «Зберегти як подання», ×; «Швидкі поля»
 * (напрям, виконавець, призначив, пріоритет, термін); «Умови · мають виконуватися всі» — рядки «Де/і · поле · умова · значення · ×» і «+ Додати умову».
 * Фільтри діють одразу (неповні рядки ігноруються); Esc ховає панель (крім Esc у відкритому діалозі чи панелі над нею).
 */
export function AdvancedPanel({ model, conditions, onChange, quick, onQuick, matches, onClose, onSaveAsView }: IAdvancedPanelProps) {
  const { t } = useTranslation();
  const { ws, rt } = model;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inDialog = e.target instanceof Element && e.target.closest('[aria-modal="true"]');
      if (e.key === 'Escape' && !e.defaultPrevented && !inDialog) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const set = (i: number, next: (typeof conditions)[number]) => onChange(conditions.map((c, j) => (j === i ? next : c)));
  return (
    <section className="lv-adv" aria-label={t('listbar.advanced')}>
      <div className="lv-adv__head">
        <strong className="lv-adv__title">{t('listbar.advanced')}</strong>
        <span className="lv-adv__meta">{matches === undefined ? t('listbar.advScope').replace(/^·\s*/, '') : `${t('listbar.advMatch', { count: matches })} ${t('listbar.advScope')}`}</span>
        <span className="lv-adv__spacer" />
        {conditions.length > 0 && <button type="button" className="lv-adv__btn lv-adv__btn--danger" onClick={() => onChange([])}>{t('listbar.clearConditions')}</button>}
        {onSaveAsView && <button type="button" className="lv-adv__btn lv-adv__btn--accent" onClick={onSaveAsView}>{t('listbar.saveAsView')}</button>}
        <button type="button" className="lv-adv__close" aria-label={t('listbar.hideAdvanced')} title={t('listbar.hideAdvanced')} onClick={onClose}>×</button>
      </div>
      <QuickFields model={model} values={quick} onChange={onQuick} />
      <div className="lv-adv__section">
        <span className="lv-adv__caption">{t('listbar.conditions')}</span>
        {conditions.map((c, i) => (
          <AdvancedRow key={i} ws={ws} rt={rt} condition={c} index={i} onChange={(next) => set(i, next)} onRemove={() => onChange(conditions.filter((_, j) => j !== i))} />
        ))}
        <button type="button" className="lv-adv__add" onClick={() => onChange([...conditions, newCondition(rt)])}>{t('listbar.addCondition')}</button>
      </div>
    </section>
  );
}
